import { HttpClient } from '@angular/common/http';

/** What the job endpoint tells a watcher. */
export interface ImportJobState {
  status: string;
  progressDone: number;
  progressTotal: number | null;
  errorMessage: string | null;
  resultJson: string | null;
}

/** What the dialog does when the watch ends, one way per ending. */
export interface ImportJobHandlers {
  /** Rows written so far, and how many there are. Called on every look that
   *  moved, so the bar only redraws when it has something to say. */
  progress(done: number, total: number | null): void;
  /** The file is written. `resultJson` is the apply's own result document. */
  done(resultJson: string | null): void;
  /** Failed or cancelled; the message is the one to show. */
  stopped(message: string): void;
  /** We gave up watching. The job did not - it is still writing, and the
   *  table will have the rows whether or not anybody is looking. */
  gaveUp(message: string): void;
  /** The watch itself broke; the import's fate is unknown from here. */
  lost(message: string): void;
}

/**
 * Watches an import's apply job to its end, reporting how far it has got.
 *
 * ## Why a watch at all
 *
 * The apply used to happen inside the request. nginx closes a proxied request
 * after sixty seconds, and a ten-thousand-row file takes about a quarter of an
 * hour - so the browser was told the import had failed while the server went
 * on writing. 1,043 rows landed under a message that said none had.
 *
 * ## The cadence, and why it is these numbers
 *
 * Taken from the rate an apply actually writes at, which is measured: about
 * ten to twenty rows a second against a database on the same host, far slower
 * across the internet. So the first look is quick - a hundred-row file is done
 * before a person has read the button - and then it settles, because a file
 * still going after ten seconds is a big one and asking twice a second buys
 * nothing but load.
 *
 * The ceiling is deliberately generous. Ten thousand rows at the slow end is
 * around a quarter of an hour, and a watch that gives up at five minutes would
 * report failure on an import that is going to succeed - which is the exact
 * mistake this whole change is undoing. When it does give up it says the job
 * is still running, because it is.
 */
export class ImportJobWatcher {
  /** Prompt looking, for as long as a small file takes. */
  static readonly FastPollMs = 500;
  static readonly FastPollAttempts = 20;      // 10 s
  /** After that it is a big file; asking often buys nothing. */
  static readonly PollMs = 2000;
  static readonly PollAttempts = 900;         // ~30 minutes at the slow cadence

  /** The wait before look number `attempt`, counting from zero. */
  static delayFor(attempt: number): number {
    return attempt < ImportJobWatcher.FastPollAttempts
      ? ImportJobWatcher.FastPollMs
      : ImportJobWatcher.PollMs;
  }

  static get maxAttempts(): number {
    return ImportJobWatcher.FastPollAttempts + ImportJobWatcher.PollAttempts;
  }

  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private lastDone = -1;

  constructor(
    private readonly http: HttpClient,
    private readonly handlers: ImportJobHandlers,
  ) {}

  /** Stop looking. The job is not touched - see `gaveUp`. */
  cancel(): void {
    this.stopped = true;
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
  }

  watch(jobId: string, attempt = 0): void {
    if (this.stopped) return;

    if (attempt >= ImportJobWatcher.maxAttempts) {
      this.handlers.gaveUp(
        'This import is taking longer than we will wait here. It is still '
        + 'running - close this and reload the table in a few minutes.');
      return;
    }

    this.timer = setTimeout(() => {
      if (this.stopped) return;
      this.http.get<ImportJobState>(`/api/automation/jobs/${jobId}`).subscribe({
        next: job => {
          if (this.stopped) return;

          // Only when it moved: a bar that redraws on every look flickers, and
          // a job between progress reports has nothing new to say.
          if (job.progressDone !== this.lastDone) {
            this.lastDone = job.progressDone;
            this.handlers.progress(job.progressDone, job.progressTotal);
          }

          switch (job.status) {
            case 'Completed':
              this.handlers.done(job.resultJson);
              return;
            case 'Failed':
              this.handlers.stopped(
                job.errorMessage || 'The import stopped before it finished.');
              return;
            case 'Canceled':
              this.handlers.stopped('The import was cancelled.');
              return;
            default:
              this.watch(jobId, attempt + 1);
          }
        },
        // One failed look is not a failed import - a proxy hiccup in the
        // middle of a quarter of an hour should not throw away the answer -
        // so keep looking and only give up when the attempts run out.
        error: () => {
          if (!this.stopped) this.watch(jobId, attempt + 1);
        },
      });
    }, ImportJobWatcher.delayFor(attempt));
  }
}
